/**
 * A new deploy's service worker has taken over (vite-plugin-pwa `autoUpdate` calls
 * `registerSW({ onNeedReload })`). Its default is `location.reload()` at once, which would yank a
 * player out of the world mid-fight. Instead: reload now when no game session is running, else
 * wait for the page to be hidden (app switch, screen lock: nobody is looking, and the reload
 * resumes the session from the saved token) or for the session to end.
 */
export interface UpdateEnv {
  inSession(): boolean;
  hidden(): boolean;
  onHidden(fn: () => void): void;
  every(ms: number, fn: () => void): void;
  reload(): void;
}

export function reloadWhenSafe(env: UpdateEnv): void {
  let done = false;
  const tryReload = (): void => {
    if (done || (env.inSession() && !env.hidden())) return;
    done = true;
    env.reload();
  };
  tryReload();
  if (done) return;
  env.onHidden(tryReload);
  env.every(5000, tryReload);
}

/** `onNeedReload` for registerSW in main.tsx; `inSession` says whether the player is in the world. */
export function deferredReload(inSession: () => boolean): () => void {
  return () =>
    reloadWhenSafe({
      inSession,
      hidden: () => document.visibilityState === "hidden",
      onHidden: (fn) =>
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "hidden") fn();
        }),
      every: (ms, fn) => void setInterval(fn, ms),
      reload: () => window.location.reload(),
    });
}

/**
 * Check for a new deploy when the app comes back to the foreground. The browser only looks for a
 * new service worker on a real navigation, and a home-screen app resumed from the background never
 * navigates, so it would keep its old build. Throttled: at most one check per `minGapMs`.
 */
export function checkOnResume(env: {
  onVisible(fn: () => void): void;
  now(): number;
  check(): void;
  minGapMs: number;
}): void {
  let last = env.now();
  env.onVisible(() => {
    if (env.now() - last < env.minGapMs) return;
    last = env.now();
    env.check();
  });
}

/** The page's own entry script (`/assets/index-<hash>.js`), from an HTML document. */
export function entryScript(html: string): string | null {
  return /\/assets\/index-[\w-]+\.js/.exec(html)?.[0] ?? null;
}

/**
 * Whether the server has a newer build than this page: compares the entry script of a fresh
 * index.html (the query string keeps the service worker's precache out of it) with ours. null:
 * unknown (offline, or a dev server without hashed bundles).
 */
export async function newerBuildAvailable(): Promise<boolean | null> {
  const mine = entryScript(document.documentElement.outerHTML);
  if (!mine) return null;
  try {
    const res = await fetch(`/?fresh=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const theirs = entryScript(await res.text());
    return theirs ? theirs !== mine : null;
  } catch {
    return null;
  }
}

/**
 * Get the latest build now: drop the service worker and its precache (and the sound cache, which
 * is small), then reload from the network. Models (~18 MB) stay cached; they refresh in the
 * background as usual. The saved login token survives, so the game resumes where it was.
 */
export async function forceUpdate(): Promise<void> {
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.filter((k) => k !== "charter-models").map((k) => caches.delete(k)));
    }
  } catch {
    // reload anyway: the worst case is the old build again
  }
  window.location.reload();
}
